import React, { useContext, useEffect, useState } from 'react';
import { AxiosContext } from '../../contexts/AxiosContext';

import {
    Doctor as DoctorType,
    PatientListItem,
    SharedPatient as SharedPatientType
} from '../../types';

import "./doctor.scss";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTable, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { ErrorText } from '../../components/Errors';
import Loading from '../../components/Loading';
import { AuthContext } from '../../contexts/AuthContext';
import { AllPatientsList, ArchivedPatientsList, Columns, SharedPatientsList, TMSPatientsList } from './PatientsList';
import { Paginated, PaginationControls } from '../../components/Pagination';


type PatientsResponse = {
    patients: Paginated<PatientListItem>,
    shared_patients: SharedPatientType[],
    doctor: DoctorType
};

type OperatorPatientsResponse = Omit<PatientsResponse, 'shared_patients'> & {
    shared_patients?: SharedPatientType[]
};

const emptyPatients: Paginated<PatientListItem> = { current_page: 1, total_pages: 1, results: [] };



function Doctor() {
    const [patients, setPatients] = useState<Paginated<PatientListItem> | null>(null);
    const [sharedPatients, setSharedPatients] = useState<SharedPatientType[] | null>(null);
    const [doctor, setDoctor] = useState<DoctorType | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [activeTab, setActiveTab] = useState<"TMS" | "ALL" | "SHARED" | "ARCHIVED">("TMS");
    const [searchInput, setSearchInput] = useState<string>("");
    const [submittedSearch, setSubmittedSearch] = useState<string>("");
    const [hasInitialLoad, setHasInitialLoad] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [sortColumn, setSortColumn] = useState<Columns | null>(null);
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
    const { sherpahGet, catchAxios } = useContext(AxiosContext)!;
    const { user } = useContext(AuthContext)!.authState;

    const getDoctor = async (query: string, page: number, sortColumn: Columns | null, sortOrder: "asc" | "desc", filter?: "tms" | "shared" | "archived", initialRequest: boolean = false) => {
        if (!user?.id) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams();
            if (filter) {
                params.set("filter", filter);
            }
            const trimmedQuery = query.trim();
            if (trimmedQuery) {
                params.set("search", trimmedQuery);
            }
            params.set("page", String(page));
            if (sortColumn) {
                params.set("sort", sortColumn);
                params.set("order", sortOrder);
            }
            if (initialRequest) {
                params.set("initial", "true");
            }

            const endpoint = user?.is_operator
                ? `/operators/${user.id}/patients${params.toString() ? `?${params.toString()}` : ""}`
                : `/doctors/${user.id}/patients${params.toString() ? `?${params.toString()}` : ""}`;

            const response = await sherpahGet<OperatorPatientsResponse | PatientsResponse>(endpoint);
            setPatients(response.data.patients);
            setSharedPatients(current => response.data.shared_patients ? response.data.shared_patients : current ?? []);
            setDoctor(response.data.doctor);
        } catch (e: any) {
            catchAxios(e, setError);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!user?.id) {
            return;
        }

        const filter = activeTab === "TMS" ? "tms" : activeTab === "SHARED" ? "shared" : activeTab === "ARCHIVED" ? "archived" : undefined;
        void getDoctor(submittedSearch, page, sortColumn, sortOrder, filter, !hasInitialLoad);
        if (!hasInitialLoad) {
            setHasInitialLoad(true);
        }
    }, [activeTab, submittedSearch, user?.id, page, sortColumn, sortOrder]);

    const handleTabClick = (tab: "TMS" | "ALL" | "SHARED" | "ARCHIVED") => {
        setActiveTab(tab);
        setPage(1);
    };

    const handleSearch = () => {
        setSubmittedSearch(searchInput);
        setPage(1);
    };

    const handleSortChange = (column: Columns) => {
        if (column === sortColumn) {
            if (sortOrder === "asc") {
                setSortOrder("desc");
            } else {
                setSortColumn(null);
                setSortOrder("asc");
            }
        } else {
            setSortColumn(column);
            setSortOrder("asc");
        }
        setPage(1);
    };

    const updatePatient = (patient: PatientListItem) => {
        setPatients(ps => ({...ps!, results: ps!.results.map(p => patient.user.id == p.user.id ? patient : p)}));
    }

    let content;
    if (error != null) {
        content = <main><ErrorText>{error}</ErrorText></main>;
    } else {
        content = <main>
            <div className="heading-wrapper">
                <h1>{doctor?.user.full_name || "Patients"}</h1>
                <div className="heading-wrapper-actions">
                    {doctor?.user.id == 37 || doctor?.user.id == 95 ? 
                        <Link to="/algorithm-data" className="w-button secondary-button fa-icon-button">
                            <FontAwesomeIcon icon={faTable} />
                            Algorithm data
                        </Link> : null }
                    <Link to="/managed-patients/add" className="w-button secondary-button fa-icon-button">
                        <FontAwesomeIcon icon={faUserPlus} />
                        Add managed patient
                    </Link>
                </div>
            </div>
            <div className="rounded-container">
                <div className="search-wrapper search-wrapper-with-button">
                    <img src="/search.png" />
                    <input
                        type="text"
                        placeholder="Search patients..."
                        value={searchInput}
                        onChange={event => setSearchInput(event.target.value)}
                        onKeyDown={event => event.key === "Enter" ? handleSearch() : null}
                    />
                    <button className="w-button secondary-button" onClick={handleSearch}>
                        Search
                    </button>
                </div>
                <div className="tabs">
                    <span className={`tab fa-icon-button ${activeTab == "TMS" ? " active-tab" : ""}`} onClick={() => handleTabClick("TMS")}>
                        TMS patients
                    </span>
                    <span className={`tab fa-icon-button ${activeTab == "ALL" ? " active-tab" : ""}`} onClick={() => handleTabClick("ALL")}>
                        All patients
                    </span>
                    { user?.is_doctor ?
                        <span className={`tab fa-icon-button ${activeTab == "SHARED" ? " active-tab" : ""}`} onClick={() => handleTabClick("SHARED")}>
                            Shared patients
                        </span> : null }
                    <span className={`tab fa-icon-button ${activeTab == "ARCHIVED" ? " active-tab" : ""}`} onClick={() => handleTabClick("ARCHIVED")}>
                        Archived patients
                    </span>
                </div>
                <div className="flex-col-gap-16">
                    <div className="patient-list-panel">
                        { loading ? <Loading /> : <>
                            { activeTab == "TMS" ? <TMSPatientsList patients={patients ?? emptyPatients} onUpdate={updatePatient} sortColumn={sortColumn} sortOrder={sortOrder} onSortChange={handleSortChange} /> : null }
                            { activeTab == "ALL" ? <AllPatientsList patients={patients ?? emptyPatients} onUpdate={updatePatient} sortColumn={sortColumn} sortOrder={sortOrder} onSortChange={handleSortChange} /> : null }
                            { activeTab == "SHARED" ? <SharedPatientsList sharedPatients={sharedPatients ?? []} /> : null }
                            { activeTab == "ARCHIVED" ? <ArchivedPatientsList patients={patients ?? emptyPatients} onUpdate={updatePatient} sortColumn={sortColumn} sortOrder={sortOrder} onSortChange={handleSortChange} /> : null }
                        </> }
                    </div>
                    <PaginationControls page={page} setPage={setPage} totalPages={patients?.total_pages ?? 1} />
                </div>
            </div>
        </main>;
    }

    return content;
}

export default Doctor;
